import { useState, useEffect, useCallback, useRef, memo } from 'react';
import { closeOnOtherPanel } from '../lib/appPanels';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from '../components/ui/sheet';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import STMark from './STMark';
import CityAssistant from './CityAssistant';
import { useFeatureFlags, isOn } from '../lib/featureFlags';
import NotificationBell from './NotificationBell';
import api, { searchAPI } from '../lib/api';

// Compare semver strings: returns -1 (a < b), 0 (equal), 1 (a > b)
import {
  MapPinned, Newspaper, Users, Store, Calendar, ClipboardList, User, LogIn,
  Star, Menu, Home, Bike, Train, Bus, Car, ChevronDown, AlertTriangle, MoreHorizontal,
  ExternalLink, Mail, Phone, Heart, Info, Search, X, Cloud, Sun, CloudRain, CloudSnow, CloudLightning, Eye, Loader2,
  CalendarDays, MapPin, ArrowRight, ArrowLeft, Music, Theater, ShoppingBag, Trophy as TrophyIcon, PartyPopper, BarChart3, Recycle, Shield, FileText, HelpCircle, CircleDot, Mountain, Landmark, Fuel, BatteryCharging, Zap, ShoppingCart, Scale, Sparkles, MessageSquarePlus, ShieldCheck, CalendarCheck, Waves, HeartHandshake, Smartphone } from 'lucide-react';
import AccessibilityPanel from './AccessibilityPanel';
import { ProfileNudge } from './ProfileNudge';
import { ink } from '../lib/utils';
import { isNativeApp, appPlatform, below as appVersionBelow } from '../lib/nativeApp';
import { safeHref } from '../lib/safeHref';
import { useScrollMemory } from '../lib/scrollMemory';
import FaqLink from './FaqLink';
import { FAQ_TOPIC_BY_PATH } from '../lib/faqLinks';
import { SEARCH_TYPE_ICONS, SEARCH_TYPE_LABELS, SEARCH_TYPE_COLORS, resultPath, mergeResults } from '../lib/searchTypes';
import { searchPageUrl } from '../lib/searchCombobox';
import { useSearchCombobox } from '../hooks/useSearchCombobox';
import OfflineBanner from './OfflineBanner';
import { useOnline } from '../lib/useOnline';

const SkylineMark = STMark;

// Only Mapa Vivo now. "Inicio" sat here too and was a second route to a place the wordmark
// already goes, while costing a row in a bar that did not fit: at 1024px the seven items needed
// 1021px inside a 955px nav, and in Modo Sénior they wrapped onto two lines. Dropping the
// redundant one is the only cut that removes no destination and re-homes nothing.
const directLinks = [
  { path: '/mapa', label: 'Mapa Vivo', icon: MapPinned },
];

const navGroups = [
  // Four groups, grouped by when a resident needs something rather than by which department owns
  // it, and mirrored in the app's Servicios sheet (mobile/src/screens/MoreScreen.js).
  //
  // There were seven. Three of them held two items each, so the menu charged a tap to reveal two
  // links, while Movilidad held nine — and laid out flat the whole thing came to 2.569px in a
  // 722px panel. The split that fixed it was moving the dated things (agenda, deportes, festivos)
  // out of "Día a día" and joining them to Ocio y turismo, which is where someone looking for
  // something to do would look. Transparencia (SomosTorre's own activity) sits under Comunidad.
  // Precio de la luz is a household question (when to run the washing machine), so it is in
  // "Día a día", next to the weather, not in "Moverse" (28 Sep 2026).
  //
  // Vecinos and Incidencias are not here on purpose: Vecinos is a permanent bottom tab, and
  // reporting an incidencia is pinned above the groups as an action, the way the app does it.
  {
    label: 'Día a día',
    icon: Cloud,
    items: [
      { path: '/tiempo', label: 'El Tiempo', icon: Cloud, desc: 'Previsión hora a hora' },
      { path: '/precio-luz', label: 'Precio Luz', icon: Zap, desc: 'PVPC hora a hora' },
      { path: '/farmacias', label: 'Farmacias', icon: Heart, desc: 'Abiertas y de guardia' },
      { path: '/residuos', label: 'Residuos', icon: Recycle, desc: 'Reciclaje y contenedores' },
      { path: '/comercio', label: 'Comercio', icon: Store, desc: 'Negocios locales' },
      { path: '/supermercados', label: 'Supermercados', icon: ShoppingCart, desc: 'Horarios y ubicaciones' },
    ],
  },
  {
    label: 'Moverse',
    icon: Bus,
    items: [
      { path: '/torrebus', label: 'Torrebus', icon: Bus, desc: 'Paradas y horarios' },
      { path: '/renfe', label: 'Cercanías', icon: Train, desc: 'Trenes a Santander' },
      { path: '/torrebici', label: 'Torrebici', icon: Bike, desc: 'Bicis y estaciones' },
      { path: '/aparcamiento', label: 'Aparcamiento', icon: Car, desc: 'Zonas y plazas libres' },
      { path: '/trafico', label: 'Tráfico', icon: AlertTriangle, desc: 'Incidencias y obras' },
      { path: '/gasolineras', label: 'Gasolineras', icon: Fuel, desc: 'Precios de combustible' },
      { path: '/puntos-carga', label: 'Puntos de Carga', icon: Zap, desc: 'Recarga eléctrica' },
      { path: '/zbe', label: 'ZBE', icon: ShieldCheck, desc: 'Zona de Bajas Emisiones' },
    ],
  },
  {
    label: 'Qué hacer',
    icon: CalendarDays,
    items: [
      { path: '/agenda', label: 'Agenda', icon: CalendarDays, desc: 'Eventos y actividades' },
      { path: '/deportes', label: 'Deportes', icon: TrophyIcon, desc: 'Partidos y equipos locales' },
      { path: '/festivos', label: 'Festivos', icon: CalendarCheck, desc: 'Días festivos' },
      { path: '/patrona', label: 'La Patrona', icon: PartyPopper, desc: 'Fiestas y peñas' },
      { path: '/playas', label: 'Playas', icon: Waves, desc: 'Estado y previsión' },
      { path: '/montanas', label: 'Montañas', icon: Mountain, desc: 'Previsión en el valle' },
      { path: '/que-ver', label: 'Qué ver', icon: MapPin, desc: 'Lugares de interés' },
      { path: '/patrimonio', label: 'Patrimonio', icon: Landmark, desc: 'Historia y monumentos' },
    ],
  },
  {
    label: 'Comunidad',
    icon: Users,
    items: [
      { path: '/vecinos', label: 'Vecinos', icon: HeartHandshake, desc: 'Ayuda entre vecinos' },
      { path: '/incidencias', label: 'Incidencias', icon: AlertTriangle, desc: 'Lo que se ha reportado' },
      // SomosTorre's own activity, not the council's accounts: with what it reports on (2026-09-24).
      { path: '/transparencia', label: 'Transparencia', icon: Search, desc: 'Qué se reporta en SomosTorre' },
      // Here since 29 Sep 2026: as a phone-menu utility only, Novedades had no way in at desktop
      // widths (the header at lg+ renders navGroups, not menuUtilities).
      { path: '/novedades', label: 'Novedades', icon: Sparkles, desc: 'Lo nuevo en SomosTorre' },
      // One entry, and navGroups feeds the desktop menu, the phone menu and the footer (2026-09-24).
      { path: '/preguntas-frecuentes', label: 'Preguntas frecuentes', icon: HelpCircle, desc: 'Cuenta, incidencias, la app…' },
    ],
  },
  {
    // Contratos públicos leads this group. It was briefly a top-level link, on the argument that
    // publishing who won which contract is the most distinctive thing this app does -- but a
    // top-level slot is also a row in a menu that has to fit one screen, and first position in
    // the group a resident opens for council business carries the point without costing that row.
    label: 'Ayuntamiento',
    icon: Landmark,
    items: [
      { path: '/contratacion', label: 'Contratos públicos', icon: FileText, desc: 'Quién gana qué, y por cuánto' },
      { path: '/tramites', label: 'Trámites', icon: ClipboardList, desc: 'Paso a paso' },
      { path: '/parcela', label: 'Urbanismo', icon: MapPinned, desc: 'Consulta de parcela y planeamiento' },
      { path: '/servicios', label: 'Teléfonos municipales', icon: Phone, desc: 'Ayuntamiento y emergencias' },
      // These two descriptions were the wrong way round in effect: "Cuentas y presupuesto" sat
      // under Transparencia, which contains no accounts and no budget — it is SomosTorre's own
      // activity (incidencias by barrio, by category, by priority, and the people reporting
      // them). The presupuesto figure is on Radiografía, under "Ingresos totales". A resident
      // looking for municipal accounts was being sent to the one page that does not have them.
      { path: '/radiografia', label: 'Radiografía', icon: BarChart3, desc: 'Padrón, empleo, renta y presupuesto' },
      { path: '/boletines', label: 'Boletines', icon: Newspaper, desc: 'BOC y avisos oficiales' },
    ],
  },
];

// Pinned above the groups, the way the app's Servicios sheet does it: the things a resident opens
// most often should not be two taps and a scroll away behind a category.
// Both lead to the place where a request starts. That is why they earn a pinned row when
// Farmacias does not -- Farmacias is one tap inside "Día a día", whereas reporting and asking for
// help are the things people come here to do. They open the list, not the form: see below.
const pinnedActions = [
  // The list pages, not ?nueva=1. That jumped straight into the new-report form, or the sign-in
  // dialog for anyone not signed in, before the reader had seen a single existing report or
  // request. Tanuj's call, matching what the app's Servicios rows already do (MoreScreen.js).
  // The pages still honour ?nueva=1 for links that mean it.
  { path: '/incidencias', label: 'Reportar una incidencia', desc: 'Baches, farolas, contenedores…', icon: AlertTriangle },
  { path: '/vecinos', label: 'Pedir ayuda a un vecino', desc: 'Alguien cerca puede echarte una mano', icon: HeartHandshake },
];

// Small, always-visible rows under the groups.
const menuUtilities = [
  // Novedades moved into Comunidad (navGroups), so it is not listed twice in the phone menu.
  // Descargas is the "get the app" page, and until now its only route in was the homepage
  // launcher — it was in neither menu, so trimming that launcher would have orphaned it. It is
  // about SomosTorre rather than about the city, so it belongs here with the other two and not
  // in a group of municipal services.
  { path: '/descargas', label: 'Descargas', icon: Smartphone },
  { path: '/perfil?feedback=open', label: 'Sugerencias', icon: MessageSquarePlus },
];


const bottomItems = [
  { path: '/', label: 'Inicio', icon: Home },
  { path: '/mapa', label: 'Mapa', icon: MapPinned },
  { path: '/vecinos', label: 'Vecinos', icon: Users },
  { path: '/perfil', label: 'Perfil', icon: User },
  { path: '/more', label: 'Más', icon: MoreHorizontal },
];

// The contextual ticker under the header: events, fuel, weather, sport, one at a time.
//
// Its own component so that its 5-second rotation re-renders only itself. The index used to be
// Layout state, so every tick re-rendered the whole header, navigation, footer and bottom tab bar
// (Layout wraps every route) to change one line of text, forever, on every page. memo so that
// Layout's own re-renders do not reach it either unless the items actually change.
const ContextTicker = memo(function ContextTicker({ items, onNavigate, onDismiss }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (items.length <= 1) return undefined;
    const interval = setInterval(() => setIdx((i) => (i + 1) % items.length), 5000);
    return () => clearInterval(interval);
  }, [items.length]);
  const item = items[idx % items.length];
  const bgColors = { fuel: 'bg-emerald-50', weather: 'bg-slate-50', event: 'bg-teal-50', sport: 'bg-amber-50' };
  const textColors = { fuel: 'text-emerald-800', weather: 'text-slate-800', event: 'text-teal-800', sport: 'text-amber-800' };
  const borderColors = { fuel: 'border-emerald-200/60', weather: 'border-slate-200/60', event: 'border-teal-200/60', sport: 'border-amber-200/60' };
  return (
    <div className={`relative ${bgColors[item.type] || 'bg-slate-50'} border-b ${borderColors[item.type] || 'border-slate-200/60'}`} data-testid="events-ticker">
      <div className="max-w-screen-xl mx-auto px-3 sm:px-6 lg:px-8 py-2 sm:py-2.5 flex items-center gap-2 sm:gap-3">
        {/* Icon + category */}
        <span className="text-sm shrink-0">{item.icon}</span>
        <span className="px-1.5 py-0.5 rounded text-[11px] sm:text-[11px] font-bold uppercase tracking-wider shrink-0"
          style={{ background: `${item.color}15`, color: ink(item.color) }}>
          {item.label}
        </span>
        {/* Content — clickable */}
        <div className="flex-1 min-w-0 cursor-pointer" key={idx}
          onClick={() => onNavigate(item.path)} data-testid="ticker-event-link">
          <div className="flex items-center gap-2 animate-slide-in-up">
            <span className={`text-xs sm:text-sm font-semibold truncate ${textColors[item.type] || 'text-slate-800'}`}>{item.text}</span>
            {item.sub && (
              <span className="text-[11px] sm:text-xs text-slate-400 shrink-0 hidden sm:inline">{item.sub}</span>
            )}
          </div>
        </div>
        {/* CTA */}
        <button onClick={() => onNavigate(item.ctaPath)}
          className="text-[11px] sm:text-[11px] font-bold px-2 py-1 sm:px-2.5 sm:py-1 rounded-full transition-all flex items-center gap-0.5 shrink-0"
          style={{ background: `${item.color}12`, color: ink(item.color) }}
          data-testid="ticker-cta">
          {item.cta} <ArrowRight size={10} />
        </button>
        {/* Close */}
        <button onClick={onDismiss}
          className="p-1 rounded-full text-slate-300 hover:text-slate-500 transition-all shrink-0"
          aria-label="Cerrar aviso" data-testid="ticker-close">
          <X size={12} />
        </button>
      </div>
    </div>
  );
});

export default function Layout({ children, user, onLogout, onShowAuth, seniorMode, toggleSeniorMode, a11y, updateA11y }) {
  const torreOn = isOn(useFeatureFlags(), 'ai_chat');
  const [a11yOpen, setA11yOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  // Page title and meta: App's PageMetaUpdater sets them for every route; calling usePageMeta here too ran it twice.
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // null = nobody has touched the accordion yet, so the group holding the current page opens
  // itself; '' = the user closed that one deliberately and wants everything shut.
  const [openMenuGroup, setOpenMenuGroup] = useState(null);
  // Desktop menu group opened from its button (keyboard or click). Hover still opens groups via CSS.
  const [openDesktopGroup, setOpenDesktopGroup] = useState(null);
  const [vecinosCount, setVecinosCount] = useState(0);
  const [weather, setWeather] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  // The server search failed (offline, 429, 500): said as such, not as "Sin resultados".
  const [searchFailed, setSearchFailed] = useState(false);
  // Escape closes the desktop dropdown without clearing what was typed; typing opens it again.
  const [dropdownDismissed, setDropdownDismissed] = useState(false);
  const online = useOnline();
  const searchFailedText = !online
    ? 'Sin conexión. La búsqueda necesita internet.'
    : searchFailed === 'rate'
      ? 'Demasiadas búsquedas seguidas. Espera un minuto y vuelve a intentarlo.'
      : 'No se ha podido buscar ahora. Comprueba la conexión e inténtalo de nuevo.';
  const mobileSearchBtnRef = useRef(null);
  const [scrolled, setScrolled] = useState(false);
  const [hasNotification, setHasNotification] = useState(false);
  const [tickerItems, setTickerItems] = useState([]);
  const dismissTicker = useCallback(() => {
    setTickerDismissed(true);
    try { sessionStorage.setItem('ticker_dismissed', '1'); } catch { /* storage unavailable */ }
  }, []);

  // Native app: the web header is hidden, so the app's top bar opens search / accessibility
  // through these events and shows the page title we report on every <title> change.
  useEffect(() => {
    if (!window.ReactNativeWebView) return undefined;
    const openSearch = () => setSearchOpen(true);
    const openA11y = () => setA11yOpen(true);
    window.addEventListener('somostorre:open-search', openSearch);
    window.addEventListener('somostorre:open-a11y', openA11y);
    // One panel at a time (lib/appPanels): search and A+ close when another header panel is asked for.
    const offSearch = closeOnOtherPanel('search', () => setSearchOpen(false));
    const offA11y = closeOnOtherPanel('a11y', () => setA11yOpen(false));
    window.__somostorreTopBar = 1;
    let last = '';
    const report = () => {
      const title = document.title.split(/ — | \| /)[0].trim();
      if (title === last) return;
      last = title;
      window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'PAGE_TITLE', title, path: window.location.pathname }));
    };
    report();
    // Watch all of <head>: the <title> node itself can be replaced
    const obs = new MutationObserver(report);
    obs.observe(document.head, { childList: true, characterData: true, subtree: true });
    return () => {
      window.removeEventListener('somostorre:open-search', openSearch);
      window.removeEventListener('somostorre:open-a11y', openA11y);
      offSearch();
      offA11y();
      obs.disconnect();
    };
  }, []);

  const [tickerDismissed, setTickerDismissed] = useState(() => {
    try { return sessionStorage.getItem('ticker_dismissed') === '1'; } catch { return false; }
  });
  const [announcement, setAnnouncement] = useState(null);
  const [appUpdateNudge, setAppUpdateNudge] = useState(null);

  // Fetch announcement banner
  useEffect(() => {
    api.get('/admin/config/announcement').then(res => {
      if (res.data?.active) setAnnouncement(res.data);
    }).catch(() => {});

    // Check app version nudge (native app only)
    if (isNativeApp()) {
      api.get('/admin/config/app-version')
        .then(res => {
          const data = res.data;
          if (!data?.min_version) return;
          // below() is true for a shell too old to name its own version, which is the case this
          // nudge exists for; it is false in a browser, where there is nothing to update.
          if (appVersionBelow(data.min_version)) {
            const storeUrl = appPlatform() === 'ios' ? data.store_url_ios : data.store_url_android;
            setAppUpdateNudge({ minVersion: data.min_version, storeUrl });
          }
        }).catch(() => {});
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // A new page opens at the top; going back returns to where the reader was (lib/scrollMemory).
  useScrollMemory();

  // A new page closes whatever sheet was over the old one. In the app, Más → «Accesibilidad y
  // tema» opens the settings panel on /accesibilidad; a tap on a tab or the back button then
  // changed the page underneath while the panel stayed on top, so the app looked frozen on it.
  useEffect(() => {
    setA11yOpen(false);
    setSearchOpen(false);
    setMobileMenuOpen(false);
    setOpenDesktopGroup(null);
  }, [location.pathname]);

  // Build ticker from /api/ticker (single lightweight call replaces 5 separate API calls)
  //
  // There were two mount effects here and both fetched /ticker. Layout wraps every route, so
  // that was a duplicate request on every page load in the app -- the same payload twice, for
  // the neighbour count and header weather in one effect and the ticker items in the other.
  // One fetch, both readers.
  useEffect(() => {
    async function buildTicker() {
      const items = [];
      try {
        const { data: d } = await api.get('/ticker');
        if (!d) { setTickerItems([]); return; }

        // The other effect's work, now reading the response we already have.
        setVecinosCount(d.vecinos_count || 0);
        if (d.hourly?.length > 0) {
          setWeather({ estado_cielo: d.hourly[0].sky_desc || '' });
        }

        // Upcoming events
        (d.events || []).forEach(ev => {
          const cat = catConfig[ev.categoria] || catConfig.general;
          items.push({
            type: 'event', label: cat.label, color: cat.color, icon: '📅',
            text: ev.titulo, sub: formatTickerDate(ev.fecha),
            path: `/agenda/${encodeURIComponent(ev.id)}`, cta: 'Agenda', ctaPath: '/agenda',
          });
        });

        // Fuel price alert
        if (d.fuel_alert) {
          const isRise = d.fuel_alert.type === 'rise';
          items.unshift({
            type: 'fuel', label: isRise ? 'Subida' : 'Bajada', color: isRise ? '#EF4444' : '#10B981', icon: '⛽',
            text: d.fuel_alert.text || 'Cambio de precio', sub: d.fuel_alert.date,
            path: '/gasolineras', cta: 'Precios', ctaPath: '/gasolineras',
          });
        }

        // Rain alert from hourly data
        const nowHour = new Date().getHours();
        const upcoming = (d.hourly || []).filter(h => h.hora >= nowHour && h.hora <= nowHour + 6 && h.precip_prob > 50);
        if (upcoming.length > 0) {
          const firstRain = upcoming[0];
          const peakProb = Math.max(...upcoming.map(h => h.precip_prob));
          const rainText = firstRain.hora <= nowHour + 1
            ? `Lluvia ahora (${peakProb}%)`
            : `Lluvia a partir de las ${firstRain.hora}:00 (${peakProb}%)`;
          items.unshift({
            type: 'weather', label: 'LLUVIA', color: '#64748B', icon: '🌧️',
            text: rainText, sub: firstRain.sky_desc || '',
            path: '/tiempo', cta: 'Tiempo', ctaPath: '/tiempo',
          });
        }

        // Sports today
        if (d.sport_today) {
          items.splice(1, 0, {
            type: 'sport', label: d.sport_today.deporte || 'Deporte', color: '#F59E0B', icon: '⚽',
            text: `${d.sport_today.equipo} vs ${d.sport_today.rival}`, sub: `${d.sport_today.hora}h`,
            path: '/deportes', cta: 'Deportes', ctaPath: '/deportes',
          });
        }
      } catch { /* silent */ }
      setTickerItems(items.slice(0, 6));
    }
    buildTicker();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced search
  // The cleanup cancels the pending timer AND marks any request already in flight as stale: the
  // debounce alone never covered a request that had started, so results for "far" could land
  // after, and replace, the results for "farmacia".
  //
  // Two sources, one list: the server's (everything Torre IA can see, backend/services/search.py)
  // and, for the preguntas frecuentes, which only the web app holds, lib/localSearch, loaded the
  // first time someone searches. Local hits go after the server's first eight, so a question
  // never pushes a stop or a pharmacy off the list.
  const localSearch = useRef(null);
  useEffect(() => {
    // Trimmed: two spaces used to send a request and answer «Sin resultados para "  "».
    if (searchQuery.trim().length < 2) { setSearchResults([]); setSearchFailed(false); return undefined; }
    let current = true;
    setSearchLoading(true);
    const timer = setTimeout(() => {
      const loadLocal = localSearch.current
        ? Promise.resolve(localSearch.current)
        : import('../lib/localSearch').then((m) => { localSearch.current = m; return m; });
      Promise.all([
        // 'rate' when the server's 60/min limit answered 429: said as such, not as a connection error.
        searchAPI.query(searchQuery).then(res => res.data?.results || [])
          .catch((err) => (err?.response?.status === 429 ? 'rate' : null)),
        loadLocal.then(m => m.searchLocal(searchQuery)).catch(() => []),
      ]).then(([remoteOrNull, local]) => {
        if (!current) return;
        const failed = remoteOrNull === 'rate' ? 'rate' : remoteOrNull === null;
        setSearchFailed(failed);
        // A question can repeat a result's title word for word; show it once (lib/searchTypes).
        setSearchResults(mergeResults(Array.isArray(remoteOrNull) ? remoteOrNull : [], local, (t) => localSearch.current?.norm(t)));
      }).finally(() => { if (current) setSearchLoading(false); });
    }, 300);
    return () => { current = false; clearTimeout(timer); };
  }, [searchQuery]);

  // Close search dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      // The mobile overlay (also the app's native search) holds its own input and results: a tap
      // inside it, even on the input itself, is not "outside".
      if (searchQuery && !e.target.closest('[data-testid="desktop-search-dropdown"]') && !e.target.closest('[data-testid="desktop-search-input"]')
        && !e.target.closest('[data-testid="mobile-search-overlay"]')) {
        setSearchResults([]);
        setSearchQuery('');
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [searchQuery]);

  const quickLinks = [
    { path: '/torrebus', label: 'Torrebus', desc: 'Horarios de autobús' },
    { path: '/renfe', label: 'Renfe Cercanías', desc: 'Trenes en vivo' },
    { path: '/tramites', label: 'Trámites', desc: 'Gestiones municipales' },
    { path: '/comercio', label: 'Comercio', desc: 'Tiendas y restaurantes' },
    { path: '/incidencias', label: 'Incidencias', desc: 'Problemas urbanos' },
    { path: '/agenda', label: 'Agenda', desc: 'Eventos y actividades' },
    { path: '/boletines', label: 'Boletines', desc: 'Noticias del BOC' },
    { path: '/residuos', label: 'Residuos', desc: 'Reciclaje y contenedores' },
  ];

  const typeIcons = SEARCH_TYPE_ICONS;
  const typeLabels = SEARCH_TYPE_LABELS;
  const typeColors = SEARCH_TYPE_COLORS;

  const handleSearchSelect = (path) => {
    navigate(path);
    setSearchOpen(false);
    setSearchQuery('');
    setSearchResults([]);
  };

  // Header search as an ARIA combobox (hooks/useSearchCombobox): the results, then «Ver todos
  // los resultados» (the /buscar page, up to 20 grouped by kind) as the last option.
  const searchReady = searchQuery.trim().length > 1 && !searchLoading && searchResults.length > 0;
  const searchOptionCount = searchReady ? searchResults.length + 1 : 0;
  const openSearchOption = (i) => {
    if (i < searchResults.length) handleSearchSelect(resultPath(searchResults[i]));
    else handleSearchSelect(searchPageUrl(searchQuery));
  };
  const submitSearch = () => { if (searchQuery.trim().length > 1) handleSearchSelect(searchPageUrl(searchQuery)); };
  const desktopOpen = searchQuery.trim().length > 1 && !dropdownDismissed;
  const desktopBox = useSearchCombobox({
    id: 'busqueda-cabecera', count: searchOptionCount, open: desktopOpen && searchOptionCount > 0,
    onSelect: openSearchOption, onSubmit: submitSearch,
    // First Escape closes the list; a second one, with the list closed, clears the field.
    onClose: () => { if (desktopOpen) setDropdownDismissed(true); else { setSearchQuery(''); setSearchResults([]); } },
  });
  // The app's back closes the top dialog by dispatching Escape on the document (lib/nativeModalBridge.js), which the
  // overlay's own onKeyDown never sees. A real Escape key lands on the focused field and is handled there.
  useEffect(() => {
    if (!searchOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && e.target === document) closeMobileSearch(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [searchOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const closeMobileSearch = () => {
    setSearchOpen(false);
    setSearchQuery('');
    setSearchResults([]);
    // Back to the button that opened it (in the app, the native top bar opened it: nothing here).
    if (mobileSearchBtnRef.current) mobileSearchBtnRef.current.focus();
  };
  const mobileBox = useSearchCombobox({
    id: 'busqueda-movil', count: searchOptionCount, open: searchOpen && searchOptionCount > 0,
    onSelect: openSearchOption, onSubmit: submitSearch, onClose: closeMobileSearch,
  });
  // What a screen reader hears as the list changes (the list itself is not read on arrival).
  const searchStatus = searchQuery.trim().length < 2 ? ''
    : searchLoading ? 'Buscando…'
    : searchResults.length ? `${searchResults.length} resultado${searchResults.length === 1 ? '' : 's'}. Usa las flechas para recorrerlos y Intro para abrir uno.`
    : searchFailed ? searchFailedText
    : `Sin resultados para "${searchQuery.trim()}"`;

  // One result row, the same in the dropdown and the overlay; `box` is the combobox it is in.
  const renderSearchOption = (item, i, box, big) => {
    const Icon = typeIcons[item.type] || Search;
    const color = typeColors[item.type] || '#64748b';
    const active = box.active === i;
    return (
      <div key={`${item.type}-${item.id}-${i}`} {...box.optionProps(i)}
        onClick={() => openSearchOption(i)}
        className={`w-full flex items-center gap-3 px-3 ${big ? 'py-3' : 'py-2.5'} rounded-lg text-left cursor-pointer transition-colors ${active ? 'bg-slate-100 ring-2 ring-inset ring-torre-teal' : 'hover:bg-slate-50'}`}
        data-testid={`${big ? 'mobile-' : ''}search-result-${i}`}>
        <div className={`${big ? 'w-9 h-9' : 'w-8 h-8'} rounded-lg flex items-center justify-center shrink-0`} style={{ background: `${color}15` }}>
          <Icon size={big ? 16 : 14} style={{ color }} aria-hidden="true" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-700 truncate">
            {item.title}
            {/* Only when the status is actually known: search results carry no opening
                hours, and `!item.is_open` labelled every pharmacy "Cerrada". */}
            {item.type === 'farmacia' && item.is_open === true && <span className="ml-1.5 text-[11px] font-bold text-emerald-600 bg-emerald-50 px-1 py-0.5 rounded">Abierta</span>}
            {item.type === 'farmacia' && item.is_open === false && <span className="ml-1.5 text-[11px] font-bold text-red-500 bg-red-50 px-1 py-0.5 rounded">Cerrada</span>}
          </p>
          <p className="text-xs text-slate-400 truncate">{item.subtitle}</p>
        </div>
        <span className="text-[11px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md shrink-0" style={{ color: ink(color), background: `${color}10` }}>
          {typeLabels[item.type] || ''}
        </span>
      </div>
    );
  };
  const renderSeeAll = (box, big) => {
    const i = searchResults.length;
    const active = box.active === i;
    return (
      <div {...box.optionProps(i)} onClick={() => openSearchOption(i)}
        className={`w-full flex items-center justify-center gap-1.5 px-3 ${big ? 'py-3' : 'py-2.5'} mt-1 rounded-lg text-sm font-bold text-torre-teal cursor-pointer border-t border-slate-100 transition-colors ${active ? 'bg-slate-100 ring-2 ring-inset ring-torre-teal' : 'hover:bg-slate-50'}`}
        data-testid={`${big ? 'mobile-' : ''}search-see-all`}>
        Ver todos los resultados <ArrowRight size={14} aria-hidden="true" />
      </div>
    );
  };

  const getWeatherIcon = () => {
    if (!weather) return null;
    const desc = (weather.estado_cielo || '').toLowerCase();
    if (desc.includes('lluvi') || desc.includes('chubasc')) return CloudRain;
    if (desc.includes('nieve')) return CloudSnow;
    if (desc.includes('torment')) return CloudLightning;
    if (desc.includes('nub') || desc.includes('cubiert')) return Cloud;
    return Sun;
  };
  const WeatherIcon = getWeatherIcon();

  // «Urbanismo» is one entry for two views (/parcela, /parcela/:ref and /urbanismo; see
  // components/parcela/UrbanismoTabs): lit on all of them.
  const isActive = (path) => location.pathname === path
    || (path === '/parcela' && /^\/(parcela\/|urbanismo$)/.test(location.pathname));
  const isGroupActive = (group) => group.items.some(i => isActive(i.path));

  const catConfig = {
    concierto: { icon: Music, color: '#ec4899', label: 'Concierto' },
    cultura: { icon: Theater, color: '#8b5cf6', label: 'Cultura' },
    teatro: { icon: Theater, color: '#8b5cf6', label: 'Teatro' },
    deporte: { icon: TrophyIcon, color: '#f59e0b', label: 'Deporte' },
    fiesta: { icon: PartyPopper, color: '#ef4444', label: 'Fiesta' },
    mercado: { icon: ShoppingBag, color: '#10b981', label: 'Mercado' },
    feria: { icon: ShoppingBag, color: '#b45309', label: 'Feria' },
    ruta: { icon: CalendarDays, color: '#15803d', label: 'Ruta' },
    cine: { icon: Theater, color: '#6366f1', label: 'Cine' },
    general: { icon: CalendarDays, color: '#0ea5e9', label: 'Evento' },
  };

  const formatTickerDate = (fecha) => {
    if (!fecha) return '';
    const d = new Date(fecha);
    return d.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' });
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Skip link — WCAG 2.4.1 Bypass Blocks. Inicio alone has ~138 focusable elements, so a
          keyboard user previously had to tab through the ticker, header and full nav on every
          page before reaching content. Visually hidden until focused. */}
      <a
        href="#contenido-principal"
        className="skip-link"
        data-testid="skip-to-content"
      >
        Saltar al contenido principal
      </a>
      {/* ── ANNOUNCEMENT BANNER ── */}
      {announcement?.active && (
        <div className={`relative border-b ${
          announcement.type === 'urgent' ? 'bg-red-600 text-white border-red-700' :
          announcement.type === 'warning' ? 'bg-amber-50 text-amber-800 border-amber-200' :
          announcement.type === 'success' ? 'bg-emerald-50 text-emerald-800 border-emerald-200' :
          'bg-blue-50 text-blue-800 border-blue-200'
        }`} data-testid="announcement-banner">
          <div className="max-w-screen-xl mx-auto px-3 sm:px-6 py-2 flex items-center gap-2">
            <p className="text-xs font-bold flex-1">{announcement.text}</p>
            {announcement.link && (
              <a href={safeHref(announcement.link)} className="text-xs font-bold underline shrink-0">Ver más</a>
            )}
            <button onClick={() => setAnnouncement(null)} className="shrink-0 opacity-60 hover:opacity-100" aria-label="Cerrar aviso">
              <X size={14} aria-hidden="true" />
            </button>
          </div>
        </div>
      )}

      {/* ── APP UPDATE NUDGE (native app only) ── */}
      {appUpdateNudge && (
        <div className="bg-indigo-600 text-white border-b border-indigo-700" data-testid="app-update-nudge">
          <div className="max-w-screen-xl mx-auto px-3 sm:px-6 py-2.5 flex items-center gap-2">
            <p className="text-xs font-bold flex-1">Nueva versión disponible. Actualiza para la mejor experiencia.</p>
            {appUpdateNudge.storeUrl && (
              <a href={safeHref(appUpdateNudge.storeUrl)} target="_blank" rel="noopener noreferrer"
                className="text-xs font-bold bg-white text-indigo-700 px-3 py-1 rounded-full shrink-0">
                Actualizar
              </a>
            )}
            <button onClick={() => setAppUpdateNudge(null)} className="shrink-0 opacity-60 hover:opacity-100" aria-label="Cerrar aviso de actualización">
              <X size={14} aria-hidden="true" />
            </button>
          </div>
        </div>
      )}

      {/* ── CONTEXTUAL TICKER — Rotates events, fuel alerts, weather, sports ── */}
      {!tickerDismissed && tickerItems.length > 0 && (
        <ContextTicker items={tickerItems} onNavigate={navigate} onDismiss={dismissTicker} />
      )}
      <OfflineBanner placement="app" />
      {/* Header — 2 row layout */}
      <header className={`sticky top-0 z-[1100] bg-white/95 border-b transition-shadow duration-300 ${scrolled ? 'shadow-sm border-slate-200/80' : 'border-slate-200/40 shadow-none'}`} data-testid="main-header">
        <OfflineBanner />
        {/* Row 1: Logo + Search + Actions */}
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* min-h, not h. At the operating system's largest text size the wordmark alone is
              wider than a 320px phone, and a fixed-height row had nowhere to put the overflow
              but off the side of the screen. A header that grows taller when someone has asked
              for bigger text is the correct answer; one that stays 56px and hides half its
              contents is not. */}
          <div className="flex items-center justify-between min-h-14 sm:min-h-[72px] py-1.5 gap-2">
            {/* Logo */}
            {/* A button, not a div with an onClick. It always should have been, but it matters
                now: with "Inicio" gone from the bar this wordmark is the only way back to the
                home page on desktop, and a div has no role, no tab stop and no Enter/Space —
                keyboard and screen-reader users simply could not reach it. */}
            <button type="button" onClick={() => navigate('/')} data-testid="logo"
              aria-label="SomosTorre — ir a la portada"
              aria-current={location.pathname === '/' ? 'page' : undefined}
              className="flex items-center gap-2.5 cursor-pointer group min-w-0 text-left rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-torre-teal focus-visible:ring-offset-2">
              <SkylineMark className="text-torre-teal transition-transform duration-200 group-hover:scale-105 shrink-0" size={36} />
              <div className="min-w-0">
                {/* The <wbr> gives the name the one break point it should ever use. Without it,
                    break-words at a big OS text size chops it wherever the line happens to end
                    ("Somos / Torr / e"); with it the wordmark splits between its two halves and
                    keeps both colours intact. break-words stays as the last resort. */}
                <h1 className="text-base font-extrabold tracking-tight text-slate-900 leading-none font-heading break-words">
                  Somos<wbr /><span className="text-torre-teal">Torre</span>
                </h1>
                <p className="text-xs font-medium tracking-wide text-slate-400 mt-0.5 hidden sm:block">Tu ciudad, sin saltos</p>
              </div>
            </button>

            {/* Desktop Search Bar — wide, centered */}
            <div className="hidden lg:flex flex-1 max-w-xl mx-8">
              <div className="relative w-full">
                <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <input type="text" value={searchQuery} onChange={(e) => { setSearchQuery(e.target.value); setDropdownDismissed(false); }}
                  onFocus={() => setDropdownDismissed(false)}
                  placeholder="Buscar incidencias, comercios, eventos..."
                  className="w-full pl-10 pr-4 py-2.5 rounded-full bg-slate-50 border border-slate-200 text-base text-slate-700 placeholder:text-slate-400 focus:outline-none focus:border-torre-teal/50 focus:ring-2 focus:ring-torre-teal/10 focus:bg-white transition-all"
                  aria-label="Buscar en SomosTorre" data-testid="desktop-search-input" {...desktopBox.inputProps} />
                {searchQuery && (
                  <button onClick={() => { setSearchQuery(''); setSearchResults([]); }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors" aria-label="Borrar búsqueda">
                    <X size={14} aria-hidden="true" />
                  </button>
                )}
                <p className="sr-only" role="status" aria-live="polite">{desktopOpen ? searchStatus : ''}</p>
                {/* Desktop dropdown results */}
                {desktopOpen && (
                  <div className="absolute top-full left-0 right-0 mt-1.5 bg-white rounded-xl shadow-xl border border-slate-200 z-50 max-h-[400px] overflow-y-auto" data-testid="desktop-search-dropdown">
                    {searchLoading ? (
                      <div className="p-4 text-center"><div className="w-5 h-5 border-2 border-torre-teal/30 border-t-torre-teal rounded-full animate-spin mx-auto" aria-hidden="true" /></div>
                    ) : searchResults.length > 0 ? (
                      <div className="p-1.5" {...desktopBox.listboxProps} aria-label="Resultados de la búsqueda">
                        {searchResults.map((item, i) => renderSearchOption(item, i, desktopBox, false))}
                        {renderSeeAll(desktopBox, false)}
                      </div>
                    ) : (
                      <div className="p-6 text-center">
                        <Search size={20} className="text-slate-200 mx-auto mb-2" aria-hidden="true" />
                        <p className="text-sm text-slate-400">{searchFailed
                          ? searchFailedText
                          : `Sin resultados para "${searchQuery.trim()}"`}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Right Actions */}
            <div className="flex items-center gap-0.5 sm:gap-2 shrink-0">
              {/* Weather Pill — hidden on mobile to save space */}
              {weather && (
                // Sky only: /ticker sends no temperature, so the number here always read "--°".
                <div className="hidden sm:flex items-center gap-1 px-2 py-1 rounded-full bg-slate-50 border border-slate-100 cursor-default" data-testid="weather-pill"
                  title={weather.estado_cielo || ''} role="img" aria-label={weather.estado_cielo ? `Cielo: ${weather.estado_cielo}` : 'El tiempo'}>
                  {WeatherIcon && <WeatherIcon size={13} className="text-torre-teal" aria-hidden="true" />}
                </div>
              )}

              {/* Mobile Search Icon */}
              <button ref={mobileSearchBtnRef} onClick={() => setSearchOpen(true)} className="lg:hidden w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-50 transition-colors" aria-label="Buscar" data-testid="mobile-search-btn">
                <Search size={16} />
              </button>

              {/* Accesibilidad: tema, alto contraste, animaciones, texto grande */}
              <button onClick={() => setA11yOpen(true)} data-testid="senior-mode-toggle"
                aria-label="Accesibilidad y pantalla" title="Accesibilidad y pantalla"
                className={`hidden sm:flex w-10 h-10 rounded-full items-center justify-center text-xs font-bold transition-all border ${
                  seniorMode || a11y?.highContrast
                    ? 'bg-torre-teal text-white border-torre-teal'
                    : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'
                }`}>
                <span className="text-[11px] font-extrabold leading-none" aria-hidden="true">A+</span>
              </button>

              {/* «Cómo empezar» (the onboarding tour) was here until 29 Sep 2026. The tour opened only from
                  this button, its copy promised things the pages no longer claim («Torrebus en tiempo
                  real», «13 trámites… escáner»), and the header needed the room. The FAQ is in
                  Comunidad. components/Onboarding.js is no longer mounted. */}

              {/* Notification Bell. Not in the app: the header is hidden there and the instance at the
                  end of this component answers the native top bar's bell. Mounted twice, both polled
                  and both opened on somostorre:open-notifications. */}
              {!(typeof window !== 'undefined' && window.__isNativeApp) && <NotificationBell user={user} />}

              {user ? (
                <div className="flex items-center gap-0.5 sm:gap-1.5">
                  {user.is_admin && (
                    <Button variant="ghost" size="sm" onClick={() => navigate('/admin')} data-testid="admin-btn"
                      className="text-slate-400 hover:text-torre-teal text-xs rounded-lg h-8 w-8 sm:h-9 sm:w-9 p-0">
                      <Shield size={15} />
                    </Button>
                  )}
                  <Badge className="hidden sm:flex items-center gap-1 bg-amber-50 text-amber-600 border-amber-200 text-xs rounded-full" data-testid="karma-badge">
                    <Star size={12} fill="currentColor" />{user.karma || 0}
                    {user.karma_level && <span className="text-[11px] text-amber-600 ml-0.5">{user.karma_level.name.split(' ').pop()}</span>}
                  </Badge>
                  <Button variant="ghost" size="sm" onClick={() => navigate('/perfil')} aria-label="Mi perfil" data-testid="profile-btn"
                    className="text-slate-500 hover:text-slate-800 text-sm rounded-lg h-8 sm:h-9 px-1 sm:px-2">
                    <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-torre-teal/10 flex items-center justify-center">
                      <User size={13} className="text-torre-teal" />
                    </div>
                    <span className="hidden sm:inline ml-1.5 text-xs font-semibold">Vecino</span>
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-1.5">
                  <Button onClick={onShowAuth} aria-label="Entrar / Registrarse" data-testid="login-btn"
                    className="bg-torre-teal hover:bg-torre-teal-hover text-white text-xs sm:text-sm font-semibold h-8 sm:h-9 px-3 sm:px-5 rounded-full">
                    <LogIn size={14} className="sm:mr-1.5" /><span className="hidden sm:inline">Entrar / Registrarse</span>
                  </Button>
                </div>
              )}
              <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <SheetTrigger asChild className="lg:hidden">
                  <Button variant="ghost" size="sm" aria-label="Abrir menú" data-testid="mobile-menu-btn" className="text-slate-500 rounded-lg h-8 w-8 sm:h-9 sm:w-9 p-0 relative">
                    <Menu size={20} />
                    {hasNotification && <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-red-500" data-testid="notification-dot" />}
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-72 bg-white border-l border-slate-200 p-0" aria-describedby={undefined}>
                  <SheetTitle className="sr-only">Menú de navegación</SheetTitle>
                  <div className="p-5 pb-4 border-b border-slate-100">
                    <div className="flex items-center gap-2.5">
                      <SkylineMark className="text-torre-teal" size={32} />
                      <div>
                        <span className="text-sm font-extrabold text-slate-900 font-heading">Somos<span className="text-torre-teal">Torre</span></span>
                        <p className="text-xs text-slate-400">Tu ciudad, sin saltos</p>
                      </div>
                    </div>
                  </div>
                  <div className="p-3 overflow-y-auto max-h-[calc(100vh-90px)]">
                    {/* Inicio and Mapa Vivo used to sit here, but both are permanent bottom tabs
                        on this breakpoint, so they were two rows spent repeating the tab bar.
                        These two are what the app's own sheet pins instead. */}
                    {pinnedActions.map((item) => (
                      <button key={item.label} onClick={() => { navigate(item.path); setMobileMenuOpen(false); }}
                        data-testid={`mobile-action-${item.label.toLowerCase().split(' ')[0]}`}
                        className="w-full px-3 py-2.5 rounded-lg text-left flex items-center gap-3 transition-all text-slate-600 hover:bg-slate-50">
                        <div className="w-8 h-8 rounded-lg bg-torre-teal/10 flex items-center justify-center shrink-0">
                          <item.icon size={15} className="text-torre-teal" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-bold text-slate-700">{item.label}</p>
                          <p className="text-xs text-slate-500 truncate">{item.desc}</p>
                        </div>
                      </button>
                    ))}
                    {/* Collapsed by default. Laid out flat, the seven groups came to 2.569px of
                        content in a 722px panel — 3,6 pantallas de scroll para un menú, with no
                        way to skip a group you did not want. Collapsed, every group is reachable
                        without scrolling; the one holding the current page opens itself. */}
                    {navGroups.map((group) => {
                      const groupHasCurrent = group.items.some((i) => isActive(i.path));
                      const expanded = openMenuGroup === null ? groupHasCurrent : openMenuGroup === group.label;
                      return (
                      <div key={group.label} className="mt-0.5">
                        <button
                          onClick={() => setOpenMenuGroup(expanded ? '' : group.label)}
                          aria-expanded={expanded}
                          aria-controls={`mobile-group-${group.label.replace(/\s/g, '-')}`}
                          data-testid={`mobile-group-${group.label.toLowerCase().replace(/\s/g, '-')}`}
                          className="w-full px-3 py-2 rounded-lg flex items-center gap-2.5 text-left hover:bg-slate-50 transition-colors">
                          <group.icon size={15} className={groupHasCurrent ? 'text-torre-teal' : 'text-slate-400'} aria-hidden="true" />
                          <span className={`flex-1 text-sm font-bold ${groupHasCurrent ? 'text-torre-teal' : 'text-slate-700'}`}>{group.label}</span>
                          <span className="text-xs text-slate-400 tabular-nums">{group.items.length}</span>
                          <ChevronDown size={15} className={`text-slate-400 transition-transform ${expanded ? 'rotate-180' : ''}`} aria-hidden="true" />
                        </button>
                        <div id={`mobile-group-${group.label.replace(/\s/g, '-')}`} hidden={!expanded} className="pl-3">
                        {group.items.map((item) => (
                          <button key={item.path} onClick={() => { navigate(item.path); setMobileMenuOpen(false); }}
                            data-testid={`mobile-nav-${item.label.toLowerCase().replace(' ', '-')}`}
                            className={`w-full px-3 py-2.5 rounded-lg text-left flex items-center gap-3 text-sm transition-all ${
                              isActive(item.path) ? 'bg-torre-teal-light text-torre-teal font-semibold' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-800'
                            }`}>
                            <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${isActive(item.path) ? 'bg-torre-teal/10' : 'bg-slate-100'}`}>
                              <item.icon size={14} className={isActive(item.path) ? 'text-torre-teal' : 'text-slate-400'} />
                            </div>
                            <div>
                              <p className="text-sm font-medium">{item.label}</p>
                              <p className="text-xs text-slate-600">{item.desc}</p>
                            </div>
                          </button>
                        ))}
                        </div>
                      </div>
                      );
                    })}
                    <div className="h-px bg-slate-100 my-2" />
                    {menuUtilities.map((item) => (
                      <button key={item.path} onClick={() => { navigate(item.path); setMobileMenuOpen(false); }}
                        data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                        className="w-full px-3 py-2 rounded-lg flex items-center gap-2.5 text-left text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors">
                        <item.icon size={15} className="text-slate-400" aria-hidden="true" />{item.label}
                      </button>
                    ))}
                    {/* About & Eolas branding in menu */}
                    <div className="mt-3 pt-3 border-t border-slate-100">
                      {/* Senior Mode Toggle in Menu */}
                      <button onClick={() => { setA11yOpen(true); setMobileMenuOpen(false); }}
                        data-testid="mobile-senior-toggle"
                        className={`w-full px-3 py-2 rounded-lg text-left flex items-center gap-2.5 text-sm font-semibold transition-all ${
                          seniorMode ? 'bg-torre-teal/10 text-torre-teal' : 'text-slate-600 hover:bg-slate-50'
                        }`}>
                        {/* min-w, not w: 15px (the other rows' icon width) is narrower than "A+", so the + wrapped under the A. */}
                        <span className={`min-w-[15px] shrink-0 whitespace-nowrap text-center text-[11px] leading-none font-extrabold ${seniorMode ? 'text-torre-teal' : 'text-slate-400'}`} aria-hidden="true">A+</span>
                        Accesibilidad
                      </button>
                      {user && (
                        <button onClick={() => { onLogout(); setMobileMenuOpen(false); }}
                          data-testid="mobile-logout-btn"
                          className="w-full px-3 py-2 rounded-lg text-left flex items-center gap-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-all">
                          <LogIn size={15} className="text-slate-400 rotate-180" aria-hidden="true" />
                          Cerrar sesión
                        </button>
                      )}
                      <button onClick={() => { navigate('/sobre'); setMobileMenuOpen(false); }}
                        data-testid="mobile-nav-sobre"
                        className="w-full px-3 py-2 rounded-lg text-left flex items-center gap-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-all">
                        <Info size={15} className="text-slate-400" aria-hidden="true" />
                        <div>
                          <p className="text-sm font-semibold">Sobre SomosTorre</p>
                        </div>
                      </button>
                      {!user && (
                        <div className="space-y-2 mt-3">
                          <button onClick={() => { setMobileMenuOpen(false); onShowAuth?.(); }}
                            data-testid="mobile-login-menu-btn"
                            className="w-full py-3 rounded-xl bg-torre-teal text-white text-sm font-bold flex items-center justify-center gap-2">
                            <LogIn size={14} /> Entrar / Registrarse
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="mt-3 mx-3 p-3 rounded-xl bg-slate-50 border border-slate-100" data-testid="mobile-menu-eolas">
                      {/* Condensed from a five-line credit block. It was 140px — a fifth of the
                          whole menu — and the footer carries the full version, with the bio and
                          the Ex-Oxford Nanopore line, on every page. The names and both links
                          stay; only the repetition goes. */}
                      <p className="text-xs text-slate-400 leading-relaxed">
                        Una iniciativa cívica gratuita de{' '}
                        <a href="https://www.linkedin.com/in/tanujgoswami/" target="_blank" rel="noopener noreferrer"
                          className="font-bold text-torre-slate hover:text-torre-teal transition-colors">Tanuj Goswami</a>
                        {' · '}
                        <a href="https://eolasprints.com" target="_blank" rel="noopener noreferrer"
                          className="font-bold text-torre-teal hover:underline">Eolas Prints</a>
                      </p>
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
        {/* Row 2: Desktop Navigation */}
        <div className="hidden lg:block border-t border-slate-100">
          <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav className="flex items-center justify-center gap-1 h-12" data-testid="desktop-nav">
              {directLinks.map((item) => (
                <button key={item.path} onClick={() => navigate(item.path)}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  className={`px-3.5 py-1.5 rounded-lg text-[13px] font-semibold transition-all flex items-center gap-1.5 ${
                    isActive(item.path) ? 'bg-torre-teal/10 text-torre-teal' : 'text-slate-500 hover:text-slate-800 hover:bg-slate-50'
                  }`}>
                  <item.icon size={15} strokeWidth={2} />{item.label}
                </button>
              ))}
              {navGroups.map((group) => {
                const groupOpen = openDesktopGroup === group.label;
                const panelId = `nav-panel-${group.label.toLowerCase().replace(/\s/g, '-')}`;
                return (
                <div key={group.label} className={`relative nav-group${groupOpen ? ' nav-group-open' : ''}`}
                  onBlur={(e) => { if (groupOpen && !e.currentTarget.contains(e.relatedTarget)) setOpenDesktopGroup(null); }}
                  // Safari does not focus a clicked button, so no blur would close a panel opened by click
                  onMouseLeave={(e) => { if (groupOpen && !e.currentTarget.contains(document.activeElement)) setOpenDesktopGroup(null); }}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape' && groupOpen) {
                      setOpenDesktopGroup(null);
                      e.currentTarget.querySelector('button')?.focus();
                    }
                  }}>
                  {/* A disclosure button: hover still opens the panel, and Enter/Space/click open
                      it for keyboard users, whose Tab otherwise never reached the items. */}
                  <button type="button" data-testid={`nav-group-${group.label.toLowerCase()}`}
                    aria-expanded={groupOpen} aria-controls={panelId}
                    onClick={() => setOpenDesktopGroup(groupOpen ? null : group.label)}
                    className={`px-3.5 py-1.5 rounded-lg text-[13px] font-semibold transition-all flex items-center gap-1.5 ${
                      isGroupActive(group) ? 'bg-torre-teal/10 text-torre-teal' : 'text-slate-500 hover:text-slate-800 hover:bg-slate-50'
                    }`}>
                    <group.icon size={15} strokeWidth={2} />{group.label}<ChevronDown size={11} className="opacity-50" />
                  </button>
                  <div id={panelId} className="nav-dropdown absolute top-full left-0 pt-2 min-w-[260px] z-50">
                    <div className="bg-white rounded-xl shadow-xl border border-slate-200 p-1.5">
                      {group.items.map((item) => (
                        <button key={item.path} onClick={() => navigate(item.path)}
                          data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all ${
                            isActive(item.path) ? 'bg-torre-teal-light text-torre-teal' : 'hover:bg-slate-50 text-slate-700'
                          }`}>
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${isActive(item.path) ? 'bg-torre-teal/15' : 'bg-slate-100'}`}>
                            <item.icon size={16} strokeWidth={2} className={isActive(item.path) ? 'text-torre-teal' : 'text-slate-400'} />
                          </div>
                          <div>
                            <p className="text-sm font-semibold">{item.label}</p>
                            <p className="text-xs text-slate-400">{item.desc}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                );
              })}
            </nav>
          </div>
        </div>
      </header>

      {/* Mobile Search Overlay */}
      {searchOpen && (
        <div className="fixed inset-0 z-[1200] bg-white" data-testid="mobile-search-overlay" role="dialog" data-state="open" aria-modal="true" aria-label="Buscar en SomosTorre"
          onKeyDown={(e) => { if (e.key === 'Escape' && e.target.getAttribute('role') !== 'combobox') closeMobileSearch(); }}>
          <div className="flex items-center gap-2 px-4 h-14 border-b border-slate-200">
            <Search size={18} className="text-slate-400 shrink-0" aria-hidden="true" />
            <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Buscar incidencias, comercios, eventos..."
              className="flex-1 text-base text-slate-700 placeholder:text-slate-400 bg-transparent border-none outline-none"
              autoFocus aria-label="Buscar en SomosTorre" data-testid="mobile-search-input" {...mobileBox.inputProps} />
            <button onClick={closeMobileSearch}
              className="w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:bg-slate-100" aria-label="Cerrar búsqueda" data-testid="mobile-search-close">
              <X size={18} aria-hidden="true" />
            </button>
          </div>
          <p className="sr-only" role="status" aria-live="polite">{searchStatus}</p>
          <div className="p-3 overflow-y-auto max-h-[calc(100vh-56px)]">
            {searchQuery.trim().length > 1 ? (
              searchLoading ? (
                <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-torre-teal/30 border-t-torre-teal rounded-full animate-spin" aria-hidden="true" /></div>
              ) : searchResults.length > 0 ? (
                <div {...mobileBox.listboxProps} aria-label="Resultados de la búsqueda">
                  {searchResults.map((item, i) => renderSearchOption(item, i, mobileBox, true))}
                  {renderSeeAll(mobileBox, true)}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Search size={24} className="text-slate-200 mx-auto mb-3" aria-hidden="true" />
                  <p className="text-sm text-slate-400">{searchFailed
                    ? searchFailedText
                    : `Sin resultados para "${searchQuery.trim()}"`}</p>
                </div>
              )
            ) : (
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500 px-3 py-2">Acceso rápido</p>
                {quickLinks.map(item => (
                  <button key={item.path} onClick={() => handleSearchSelect(item.path)}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left hover:bg-slate-50 transition-colors">
                    <div className="w-7 h-7 rounded-lg bg-slate-50 flex items-center justify-center shrink-0">
                      <Search size={12} className="text-slate-400" aria-hidden="true" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-600 font-medium">{item.label}</p>
                      <p className="text-[11px] text-slate-400">{item.desc}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <main id="contenido-principal" tabIndex={-1} className="flex-1 pb-20 lg:pb-0">
        {/* Back to home — all pages except homepage */}
        {!isHomePage && (
          <div className="max-w-screen-xl mx-auto px-3 sm:px-6 lg:px-8 pt-2 sm:pt-3 pb-2 sm:pb-3">
            <button
              // On the first entry of this tab's history (a shared link, a fresh tab) there is
              // nothing behind to go back to: -1 would leave the site or do nothing. Go home instead.
              onClick={() => ((window.history.state?.idx ?? 0) > 0 ? navigate(-1) : navigate('/'))}
              className="inline-flex items-center gap-1.5 text-xs sm:text-sm text-slate-400 hover:text-torre-teal font-medium transition-colors py-1"
              data-testid="back-button"
            >
              <ArrowLeft size={14} />
              Volver
            </button>
          </div>
        )}
        {children}
        {FAQ_TOPIC_BY_PATH[location.pathname] && (
          <FaqLink topic={FAQ_TOPIC_BY_PATH[location.pathname]} className="max-w-screen-xl mx-auto px-4 pb-4" />
        )}
      </main>
      {/* Torre's button goes while the admin has Torre paused (the server answers «en pausa»). */}
      {torreOn && <CityAssistant user={user} />}

      {/* ── Rich Footer ── */}
      <footer className="on-dark bg-torre-slate pb-24 lg:pb-0" data-testid="app-footer">
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">

            {/* Column 1 — About */}
            <div data-testid="footer-about">
              <div className="flex items-center gap-2.5 mb-3">
                <SkylineMark className="text-torre-teal" size={36} />
                <div>
                  <span className="text-base font-extrabold text-white font-heading">Somos<span className="text-torre-teal">Torre</span></span>
                  <p className="text-xs text-slate-300 mt-0.5">Tu ciudad, sin saltos</p>
                </div>
              </div>
              <p className="text-sm text-slate-300 leading-relaxed mb-4">
                SomosTorre es una iniciativa cívica gratuita creada por <strong className="text-white">Tanuj Goswami</strong> (ex-Oxford Nanopore Technologies) y <strong className="text-white">Eolas Prints</strong> para hacer la vida más fácil a los vecinos de Torrelavega. Tecnología al servicio de tu ciudad.
              </p>
              <a href="https://eolasprints.com" target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-torre-teal text-white text-xs font-bold hover:bg-torre-teal-hover transition-colors"
                data-testid="footer-eolas-link">
                Conoce Eolas Prints <ExternalLink size={12} />
              </a>
              <div className="flex flex-wrap items-center gap-2 mt-4" data-testid="footer-app-badges">
                <a href="https://apps.apple.com/us/app/somostorre/id6760478093" target="_blank" rel="noopener noreferrer"
                  className="inline-flex max-w-full items-center gap-2 px-3.5 py-2 rounded-full bg-white/10 text-white text-xs font-bold hover:bg-white/20 transition-colors border border-white/15"
                  data-testid="footer-appstore-link">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
                  App Store
                </a>
                <a href="https://play.google.com/store/apps/details?id=com.somostorre.app" target="_blank" rel="noopener noreferrer"
                  className="inline-flex max-w-full items-center gap-2 px-3.5 py-2 rounded-full bg-white/10 text-white text-xs font-bold hover:bg-white/20 transition-colors border border-white/15"
                  data-testid="footer-playstore-link">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 0 1-.61-.92V2.734a1 1 0 0 1 .609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.199l2.302 2.302a1 1 0 0 1 0 1.38l-2.302 2.302L15.303 12l2.395-2.492zM5.864 2.658L16.8 8.99l-2.302 2.302-8.634-8.634z"/></svg>
                  Google Play
                </a>
              </div>
              <a href="https://www.instagram.com/somos.torre" target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-2 mt-3 text-slate-300 hover:text-white text-xs font-medium transition-colors"
                data-testid="footer-instagram-link">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>
                @somos.torre
              </a>
              {vecinosCount > 0 && (
                <p className="text-xs text-slate-300 mt-4 flex items-center gap-1.5" data-testid="footer-vecinos-count">
                  <Users size={12} className="text-torre-teal" />
                  <span><strong className="text-white">{vecinosCount} vecinos</strong> ya confían en SomosTorre</span>
                </p>
              )}
              <Link to="/perfil?feedback=open" className="inline-flex items-center gap-2 mt-4 pt-4 border-t border-white/10 text-slate-300 hover:text-white text-xs font-medium transition-colors" data-testid="footer-feedback-link">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                Sugerencias y errores
              </Link>
            </div>

            {/* Column 2 — Explore (categorized) */}
            {/* Derived from navGroups, not a hand-written list.
                The footer had invented a third taxonomy — "Movilidad / Comunidad / Ciudad" —
                alongside the header's seven groups and the app's Servicios sheet, so the same
                page sat under a different heading depending on where you looked, and new pages
                had to be remembered in three places. Deportes and Novedades had already been
                forgotten here. One source now, in the order the header uses. */}
            <div data-testid="footer-explore" className="sm:col-span-2 lg:col-span-1">
              <h4 className="text-xs font-extrabold uppercase tracking-wider text-slate-300 mb-3">Explora</h4>
              <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                {navGroups.map((group) => (
                  <div key={group.label} className="min-w-0">
                    <p className="text-[11px] font-bold uppercase tracking-wider text-torre-teal mb-1.5">{group.label}</p>
                    {group.items.map((link) => (
                      // w-full is load-bearing, not decoration. A <button> resolves width:auto to
                      // shrink-to-fit even with display:block, so inside this 2-column grid the
                      // button sized itself to its unbroken label instead of to its 132px track,
                      // and break-words never got a chance to act — the word already fit its own
                      // over-wide box. At someone else's 200% text size "Aparcamiento" was 182px
                      // in a 132px column and ran 34px off the right of every page on the site,
                      // because this footer is on every page. w-full pins it to the track.
                      <button key={link.path} onClick={() => navigate(link.path)}
                        className="block w-full text-sm text-slate-300 hover:text-torre-teal transition-colors text-left py-0.5 break-words"
                        data-testid={`footer-nav-${link.label.toLowerCase().replace(/\s/g, '-')}`}>
                        {link.label}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            </div>

            {/* Column 3 — Contact & Legal */}
            <div data-testid="footer-contact-legal">
              <h4 className="text-xs font-extrabold uppercase tracking-wider text-slate-300 mb-3">Contacto y Legal</h4>
              <div className="space-y-2 mb-4">
                <a href="mailto:info@somostorre.com" className="flex items-center gap-2 text-sm text-slate-300 hover:text-torre-teal transition-colors">
                  <Mail size={14} className="shrink-0" /> info@somostorre.com
                </a>
                <a href="tel:+34942735955" className="flex items-center gap-2 text-sm text-slate-300 hover:text-torre-teal transition-colors">
                  <Phone size={14} className="shrink-0" /> +34 942 735 955
                </a>
              </div>
              <div className="flex flex-wrap gap-x-3 gap-y-1">
                <button onClick={() => navigate('/legal?tab=privacidad')} className="text-xs text-slate-300 hover:text-torre-teal transition-colors" data-testid="footer-privacy-link">Privacidad</button>
                <button onClick={() => navigate('/legal?tab=legal')} className="text-xs text-slate-300 hover:text-torre-teal transition-colors" data-testid="footer-legal-link">Aviso Legal</button>
                <button onClick={() => navigate('/legal?tab=cookies')} className="text-xs text-slate-300 hover:text-torre-teal transition-colors" data-testid="footer-cookies-link">Cookies</button>
                <button onClick={() => navigate('/accesibilidad')} className="text-xs text-slate-300 hover:text-torre-teal transition-colors" data-testid="footer-accesibilidad-link">Accesibilidad</button>
              </div>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="mt-8 pt-6 border-t border-slate-700/50 flex flex-col items-center gap-2.5 text-center" data-testid="footer-bottom-bar">
            <p className="text-xs text-slate-300">
              © 2026 SomosTorre / <a href="https://www.linkedin.com/in/tanujgoswami/" target="_blank" rel="noopener noreferrer" className="text-white hover:text-torre-teal font-semibold transition-colors">Tanuj Goswami</a> y <a href="https://eolasprints.com" target="_blank" rel="noopener noreferrer" className="text-white hover:text-torre-teal font-semibold transition-colors">Eolas Prints, S.L.</a> Todos los derechos reservados.
            </p>
            <p className="text-[11px] text-slate-300 max-w-lg leading-relaxed">
              El diseño, código fuente y la estructuración de la base de datos son propiedad exclusiva de sus creadores. Los datos brutos mostrados provienen de fuentes públicas y abiertas (DGT, AEMET, MITECO, etc.). SomosTorre es una iniciativa tecnológica cívica e independiente, no afiliada a ningún organismo gubernamental.
            </p>
            <p className="text-xs text-slate-300">Tu ciudad, sin saltos.</p>
          </div>
        </div>
      </footer>

      {/* Mobile Bottom Nav — 5 tabs */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-[1100] bg-white border-t border-slate-200" data-testid="mobile-bottom-nav" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
        <div className="flex items-center justify-around h-16">
          {bottomItems.map((item) => {
            const isMapa = item.path === '/mapa';
            const isMore = item.path === '/more';
            const active = isMore ? mobileMenuOpen : isActive(item.path);
            return (
              <button key={item.path}
                onClick={() => isMore ? setMobileMenuOpen(true) : navigate(item.path)}
                data-testid={`bottom-nav-${item.label.toLowerCase().replace(' ', '-')}`}
                className={`flex flex-col items-center justify-center gap-0.5 flex-1 min-w-0 basis-0 h-full px-0.5 transition-all ${
                  active ? 'text-torre-teal' : 'text-slate-400'
                }`}>
                {isMapa ? (
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center -mt-3 ${active ? 'bg-torre-teal text-white shadow-lg shadow-torre-teal/25' : 'bg-slate-100 text-slate-500'}`}>
                    <item.icon size={20} strokeWidth={2} />
                  </div>
                ) : (
                  <item.icon size={22} strokeWidth={active ? 2.5 : 1.8} />
                )}
                {/* Truncate rather than push. Five tabs each claiming min-w-[56px] add up to more
                    than a 320px screen as soon as the label is set at someone's 300% text size,
                    and a fixed bottom bar cannot scroll — it just leaves its last tab off the
                    edge. Each tab now takes an equal share of the bar and the label gives way;
                    the icon and the accessible name are unaffected. */}
                <span className={`text-xs max-w-full truncate ${active ? 'font-bold' : 'font-medium'}`}>{item.label}</span>
              </button>
            );
          })}
        </div>
      </nav>

      {a11y && <AccessibilityPanel open={a11yOpen} onOpenChange={setA11yOpen} settings={a11y} update={updateA11y} />}

      {/* Profile Nudge — shows for incomplete profiles */}
      {user && <ProfileNudge user={user} onOpenProfile={() => navigate('/perfil')} />}

      {/* In the app the header above is display:none, which took the notification bell with it —
          push arrived and badged the icon, and there was no way to read what it said. This
          instance lives outside the header with its trigger hidden; the bell in the native top
          bar opens it. */}
      {typeof window !== 'undefined' && window.__isNativeApp && <NotificationBell user={user} inApp />}
    </div>
  );
}
